import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { SunkevLogo } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";
import { formatApiErrorDetail } from "@/context/AuthContext";

import { API } from "@/lib/api";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: form.password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10";

  return (
    <section className="flex min-h-screen items-center justify-center bg-mist px-5 pb-16 pt-28">
      <Reveal className="w-full max-w-md">
        <div className="rounded-3xl border border-gray-100 bg-white p-10 shadow-xl shadow-navy/5">
          <div className="flex justify-center">
            <SunkevLogo compact />
          </div>
          {done ? (
            <div className="mt-8 text-center" data-testid="reset-done">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </span>
              <h1 className="mt-6 font-heading text-2xl font-bold text-ink">Password updated</h1>
              <p className="mt-3 text-sm text-fog">
                You can now sign in with your new password.
              </p>
            </div>
          ) : (
            <>
              <h1 className="mt-8 text-center font-heading text-2xl font-bold text-ink">
                Set a new password
              </h1>
              <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="reset-form">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="New password (min. 8 characters)"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    data-testid="reset-password-input"
                    className={inputCls}
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Confirm new password"
                    value={form.confirm}
                    onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                    data-testid="reset-confirm-input"
                    className={inputCls}
                  />
                </div>
                {error && (
                  <p data-testid="reset-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy || !token}
                  data-testid="reset-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
                </button>
              </form>
            </>
          )}
          <p className="mt-8 text-center text-sm text-fog">
            <Link to="/login" className="font-semibold text-royal hover:text-navy">
              Back to sign in
            </Link>
          </p>
        </div>
      </Reveal>
    </section>
  );
};

export default ResetPassword;
